import React, { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";

/**
 * Multi-select combobox using shadcn Command + Popover.
 * Props:
 * - options: string[]
 * - values: string[]
 * - onChange: (values) => void
 * - placeholder
 */
export default function MultiSelect({ options = [], values = [], onChange, placeholder = "Select...", disabled = false, testid = "multi-select" }) {
  const [open, setOpen] = useState(false);

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full min-h-10 flex items-start justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-sm transition-colors",
            "hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring",
            disabled && "opacity-60 cursor-not-allowed"
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {values.length === 0 && (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            {values.slice(0, 6).map((v) => (
              <Badge
                key={v}
                variant="secondary"
                className="text-xs"
                onClick={(e) => { e.stopPropagation(); toggle(v); }}
              >
                {v}
                <X className="ml-1 h-3 w-3" />
              </Badge>
            ))}
            {values.length > 6 && (
              <Badge variant="secondary" className="text-xs">+{values.length - 6} more</Badge>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 mt-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] bg-popover" align="start">
        <Command>
          <CommandInput placeholder="Search..." data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const selected = values.includes(opt);
                return (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => toggle(opt)}
                    data-testid={`${testid}-option-${opt}`}
                  >
                    <div className={cn("mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary", selected ? "bg-primary text-primary-foreground" : "opacity-50")}>
                      {selected && <Check className="h-3 w-3" />}
                    </div>
                    <span>{opt}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
